<template>
  <div class="page-container">
    <!-- 统计卡片 -->
    <el-row :gutter="16" style="margin-bottom:16px">
      <el-col :xs="12" :sm="6">
        <el-card shadow="never" class="stat-card">
          <div class="stat-value">{{ stats.today_count }}</div>
          <div class="stat-label">今日订单数</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="6">
        <el-card shadow="never" class="stat-card">
          <div class="stat-value primary">¥ {{ stats.today_amount.toFixed(2) }}</div>
          <div class="stat-label">今日成交额</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="6">
        <el-card shadow="never" class="stat-card">
          <div class="stat-value">{{ stats.total_count }}</div>
          <div class="stat-label">累计订单数</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="6">
        <el-card shadow="never" class="stat-card">
          <div class="stat-value primary">¥ {{ stats.total_amount.toFixed(2) }}</div>
          <div class="stat-label">累计成交额</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 搜索栏 -->
    <el-card class="search-card" shadow="never">
      <el-form :inline="true" :model="searchForm" size="small">
        <el-form-item label="订单号/交易号">
          <el-input
            v-model="searchForm.keyword"
            placeholder="订单号或第三方交易号"
            clearable
            style="width:220px"
          />
        </el-form-item>
        <el-form-item label="套餐类型">
          <el-select v-model="searchForm.package_type" placeholder="全部" clearable style="width:110px">
            <el-option label="余额套餐" value="balance" />
            <el-option label="积分套餐" value="points" />
          </el-select>
        </el-form-item>
        <el-form-item label="支付方式">
          <el-select v-model="searchForm.pay_type" placeholder="全部" clearable style="width:110px">
            <el-option label="支付宝" :value="1" />
            <el-option label="微信支付" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item label="订单状态">
          <el-select v-model="searchForm.status" placeholder="全部" clearable style="width:110px">
            <el-option v-for="(label, val) in statusLabels" :key="val" :label="label" :value="Number(val)" />
          </el-select>
        </el-form-item>
        <el-form-item label="用户ID">
          <el-input v-model="searchForm.user_id" placeholder="用户ID" clearable style="width:100px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="mini" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button size="mini" icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 订单列表 -->
    <el-card shadow="never" style="margin-top:12px">
      <div slot="header" class="card-header">
        <span>订单列表</span>
      </div>

      <el-table :data="tableData" v-loading="loading" border stripe size="small">
        <el-table-column prop="order_no" label="订单号" min-width="200" show-overflow-tooltip />
        <el-table-column prop="user_id" label="用户ID" width="80" align="center" />
        <el-table-column label="套餐" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <el-tag :type="row.package_type === 'balance' ? 'warning' : 'success'" size="mini" style="margin-right:4px">
              {{ row.package_type === 'balance' ? '余额' : '积分' }}
            </el-tag>
            {{ row.package_title }}
          </template>
        </el-table-column>
        <el-table-column label="实付（元）" width="100" align="right">
          <template slot-scope="{ row }">
            <span class="price-text">¥ {{ row.price.toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="到账数量" width="100" align="right">
          <template slot-scope="{ row }">
            {{ row.package_type === 'points' ? parseInt(row.amount) + ' 积分' : row.amount.toFixed(2) + ' 元' }}
          </template>
        </el-table-column>
        <el-table-column label="支付方式" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.pay_type === 1 ? 'primary' : 'success'" size="mini">
              {{ row.pay_type === 1 ? '支付宝' : '微信支付' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagMap[row.status]" size="mini">
              {{ statusLabels[row.status] }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="paid_at" label="支付时间" width="165" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.paid_at || '—' }}
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="下单时间" width="165" />
        <el-table-column label="操作" width="80" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-permission="['sale:order:detail']"
              type="text"
              size="mini"
              @click="handleDetail(row)"
            >详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          background
          :current-page="page"
          :page-size="pageSize"
          :page-sizes="[15, 30, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next"
          @size-change="sz => { pageSize = sz; page = 1; fetchData() }"
          @current-change="p => { page = p; fetchData() }"
        />
      </div>
    </el-card>

    <!-- 订单详情弹窗 -->
    <el-dialog title="订单详情" :visible.sync="detailVisible" width="560px">
      <el-descriptions v-if="detailData" :column="2" border size="small">
        <el-descriptions-item label="订单号" :span="2">
          <span class="mono">{{ detailData.order_no }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="第三方交易号" :span="2">
          <span class="mono">{{ detailData.trade_no || '—' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="用户ID">{{ detailData.user_id }}</el-descriptions-item>
        <el-descriptions-item label="订单状态">
          <el-tag :type="statusTagMap[detailData.status]" size="mini">
            {{ statusLabels[detailData.status] }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="套餐类型">
          <el-tag :type="detailData.package_type === 'balance' ? 'warning' : 'success'" size="mini">
            {{ detailData.package_type === 'balance' ? '余额套餐' : '积分套餐' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="套餐名称">{{ detailData.package_title }}</el-descriptions-item>
        <el-descriptions-item label="实付金额">
          <span class="price-text">¥ {{ detailData.price ? detailData.price.toFixed(2) : '0.00' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="到账数量">
          {{ detailData.package_type === 'points'
              ? parseInt(detailData.amount) + ' 积分'
              : (detailData.amount ? detailData.amount.toFixed(2) : '0.00') + ' 元' }}
        </el-descriptions-item>
        <el-descriptions-item label="支付方式">{{ detailData.pay_type_label }}</el-descriptions-item>
        <el-descriptions-item label="支付时间">{{ detailData.paid_at || '—' }}</el-descriptions-item>
        <el-descriptions-item label="下单时间">{{ detailData.created_at }}</el-descriptions-item>
        <el-descriptions-item label="更新时间">{{ detailData.updated_at }}</el-descriptions-item>
        <el-descriptions-item v-if="detailData.remark" label="备注" :span="2">
          {{ detailData.remark }}
        </el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </div>
</template>

<script>
import { getOrderStats, getOrderList, getOrderDetail } from '@/api/order'

export default {
  name: 'SaleOrder',
  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      page: 1,
      pageSize: 15,
      stats: {
        today_count: 0,
        today_amount: 0,
        total_count: 0,
        total_amount: 0
      },
      statusLabels: { 0: '待支付', 1: '已支付', 2: '已取消', 3: '已退款' },
      statusTagMap: { 0: 'warning', 1: 'success', 2: 'info', 3: 'danger' },
      searchForm: {
        keyword: '',
        package_type: '',
        pay_type: '',
        status: '',
        user_id: ''
      },
      detailVisible: false,
      detailData: null
    }
  },
  created() {
    this.fetchStats()
    this.fetchData()
  },
  methods: {
    async fetchStats() {
      try {
        const res = await getOrderStats()
        this.stats = res.data || this.stats
      } catch {}
    },

    async fetchData() {
      this.loading = true
      try {
        const params = { ...this.searchForm, page: this.page, pageSize: this.pageSize }
        // 过滤掉空值
        Object.keys(params).forEach(k => {
          if (params[k] === '' || params[k] === null || params[k] === undefined) {
            delete params[k]
          }
        })
        const res = await getOrderList(params)
        this.tableData = res.data.list  || []
        this.total     = res.data.total || 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.page = 1
      this.fetchData()
    },

    handleReset() {
      this.searchForm = { keyword: '', package_type: '', pay_type: '', status: '', user_id: '' }
      this.page = 1
      this.fetchData()
    },

    async handleDetail(row) {
      try {
        const res = await getOrderDetail({ order_no: row.order_no })
        this.detailData = res.data
        this.detailVisible = true
      } catch {}
    }
  }
}
</script>

<style scoped>
.page-container { padding: 16px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.pagination-wrap { margin-top: 16px; text-align: right; }

.stat-card { text-align: center; padding: 8px 0; }
.stat-value { font-size: 28px; font-weight: 700; color: #303133; line-height: 1.4; }
.stat-value.primary { color: #409eff; }
.stat-label { font-size: 13px; color: #909399; margin-top: 4px; }

.price-text { color: #f56c6c; font-weight: 600; }
.mono { font-family: monospace; font-size: 13px; }
</style>
